import type { ReactElement, ReactNode } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { RequireAuth } from './session/RequireAuth'
import { Login } from './routes/Login'
import { Logout } from './routes/Logout'
import { Pricing } from './routes/Pricing'
import { Paywall } from './routes/Paywall'
import { CheckoutCancel, CheckoutSuccess } from './routes/Checkout'
import { NotFound } from './routes/NotFound'

/**
 * The route table every product shares — sign-in, pricing, paywall, checkout
 * returns, 404 — around the product's own screens.
 *
 *   <PlatformApp layout={<AppLayout />}>
 *     <Route index element={<Home />} />
 *     <Route path="settings" element={<Settings />} />
 *   </PlatformApp>
 *
 * `children` are <Route>s rendered inside the guarded layout, so each inherits
 * the auth + subscription gate and the shell in one place. `publicRoutes` are
 * extra top-level <Route>s (redirect aliases and the like).
 */
export function PlatformApp({
  layout,
  children,
  publicRoutes,
}: {
  layout: ReactElement
  children: ReactNode
  publicRoutes?: ReactNode
}) {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/paywall" element={<Paywall />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel" element={<CheckoutCancel />} />

        <Route element={<RequireAuth>{layout}</RequireAuth>}>{children}</Route>

        {publicRoutes}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  )
}
